/**
 * TableModeContent
 *
 * Table-mode body of the composite field sidebar, rendered by
 * `CompositeFieldTypeContent` when the sidebar was opened from a table cell.
 * It subscribes with `useSnapshot(cellProxy, { sync: true })` to the same
 * Valtio cell proxy `ArCell` renders from, so drawer edits and the underlying
 * cell stay in step within the same tick.
 * The sub-fields a Form Derive projection fills travel the same way the formula
 * sequence does — through the module-level sidebar state, set at open time — and
 * end up disabled beside the formula-computed ones, each carrying the sentence
 * that says why.
 */

import { FC } from 'react';
import { useSnapshot } from 'valtio';
import type { CompositeFieldValue, UIElement } from '#types/System.types';
import { CompositeFieldProvider } from '../CompositeFieldProvider';
import LayoutUI from '../../rendering/LayoutUI';
import {
  getCellValueProxy,
  getDerivedSubFieldReasons,
  getOnChange,
  getFormulaSequence,
} from '../compositeFieldSidebarState';

type TableModeContentProps = {
  cellProxy: NonNullable<ReturnType<typeof getCellValueProxy>>;
  compositeFieldTypeId: string;
  fieldId: string;
  isFormulaColumn: boolean;
  onChange: ReturnType<typeof getOnChange>;
  structure: UIElement | undefined;
};

/** Table mode: subscribes to the same Valtio cell proxy that ArCell uses. */
export const TableModeContent: FC<TableModeContentProps> = ({
  cellProxy,
  compositeFieldTypeId,
  fieldId,
  isFormulaColumn,
  onChange,
  structure,
}) => {
  const cellSnap = useSnapshot(cellProxy, { sync: true });
  const value = cellSnap.value as CompositeFieldValue | undefined;
  const formulaSequence = getFormulaSequence();
  const derivedSubFieldReasons = getDerivedSubFieldReasons();

  if (!structure) return null;

  return (
    <CompositeFieldProvider
      compositeFieldId={fieldId}
      compositeFieldTypeId={compositeFieldTypeId}
      value={value}
      isFormulaColumn={isFormulaColumn}
      formulaSequence={formulaSequence}
      derivedSubFieldReasons={derivedSubFieldReasons}
      onChange={onChange}
      tableMode
    >
      <LayoutUI uiElement={structure} />
    </CompositeFieldProvider>
  );
};
